import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Play, Pause, Square, SkipBack, SkipForward, Circle, Sparkles,
  FolderOpen, Download, Send, AudioWaveform, Scissors, Copy,
  Layers3, Keyboard, Trash2, ZoomIn, ZoomOut, RotateCcw, Maximize2,
  Megaphone, ArrowLeft,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { TransportPlayer } from "@/lib/psAudio";
import s from "./ProductionSuite.module.css";
import { PSMixer, PSFXChain } from "@/components/production/PSMixerPanel";
import PSAssetLibrary from "@/components/production/PSAssetLibrary";
import { PSProjectsModal, PSExportModal } from "@/components/production/PSModals";
import PSMusicImportModal from "@/components/production/PSMusicImportModal";
import PSShortcutHelp from "@/components/production/PSShortcutHelp";
import StemSeparateModal from "@/components/production/StemSeparateModal";
import PSTrackHeaders from "@/components/production/PSTrackHeaders";
import usePSTrackReorder from "@/hooks/usePSTrackReorder";
import { appPrompt, appConfirm } from "@/components/AppDialogHost";

const MIN_PROJECT_DURATION = 30; // seconds — minimum visible timeline window
const TIMELINE_TAIL_BUFFER  = 5;  // seconds of slack after the last clip
// Timeline zoom is user-controlled (Ctrl +/-/0 + toolbar buttons). Values
// are pixels-per-second — smaller = zoom-out (see more of the mix),
// larger = zoom-in (precise edits). Reset default is 50 px/s to match
// the pre-zoom hard-coded scale.
const ZOOM_LEVELS = [8, 12, 20, 30, 50, 75, 100, 150, 200];
const DEFAULT_ZOOM = 50;
const ZOOM_STORAGE_KEY = "bfa-ps-zoom";

const DEFAULT_TRACKS = [
  { id: "vo",     name: "Station VO",    type: "Voice · mono",    color: "#4a9eff" },
  { id: "bed",    name: "Music bed",     type: "Audio · stereo",  color: "#3ecf8e" },
  { id: "fx",     name: "FX layer",      type: "Audio · stereo",  color: "#a78bfa" },
  { id: "jingle", name: "Jingle / ID",   type: "Audio · stereo",  color: "#f59e0b" },
  { id: "sfx",    name: "SFX / Stinger", type: "Audio · stereo",  color: "#ff6b6b" },
];

// NOTE: The 10 demo clips that used to pre-populate the timeline
// (Intro VO, Reverb tail, Smooth Jazz Bed, Swoosh, etc.) were removed
// because their WebAudio synth playback couldn't be reliably stopped
// on some browsers — leaving a hanging reverb-tail ring after users
// clicked Pause. New sessions now open with an empty timeline; drop
// real clips in from the Asset Library, Stem Studio, or Music import.
const DEFAULT_CLIPS = [];

const DEFAULT_VOLUMES = { vo: 85, bed: 62, fx: 45, jingle: 78, sfx: 55, master: 90 };

const DEFAULT_FX = {
  vo: [
    { name: "EQ — Parametric", value: "+3dB @ 2kHz",   color: "#4a9eff", enabled: true },
    { name: "Compressor",      value: "4:1 · -18dB",   color: "#3ecf8e", enabled: true },
    { name: "Reverb — Room",   value: "12% wet",        color: "#a78bfa", enabled: true },
    { name: "Limiter",         value: "-0.3 dBFS",      color: "#f59e0b", enabled: true },
  ],
  bed: [
    { name: "EQ — High shelf", value: "-2dB @ 8kHz",    color: "#4a9eff", enabled: true },
    { name: "Compressor",      value: "2:1 · -24dB",    color: "#3ecf8e", enabled: true },
  ],
  fx: [], jingle: [], sfx: [],
};

function fmt(seconds) {
  const m = Math.floor(seconds / 60);
  const s2 = Math.floor(seconds % 60);
  const tenths = Math.floor((seconds % 1) * 10);
  return `${String(m).padStart(2, "0")}:${String(s2).padStart(2, "0")}.${tenths}`;
}

// Stable mini-waveform heights per clip — deterministic so the bars don't
// jitter on every re-render. Uses a tiny hash of (id + index).
// Update #146 — cache the generated array per clip.id and cap the count
// to keep the DOM light. A 155-second clip at 50px/s would otherwise
// render ~1900 wave bars, murdering drag performance.
const _WAVE_CACHE = new Map();
const MAX_WAVE_BARS = 120;
function waveHeights(clipId, count) {
  const n = Math.min(MAX_WAVE_BARS, Math.max(8, count | 0));
  const key = `${clipId}:${n}`;
  const cached = _WAVE_CACHE.get(key);
  if (cached) return cached;
  const out = [];
  let h = 0;
  for (let i = 0; i < clipId.length; i++) h = (h * 31 + clipId.charCodeAt(i)) | 0;
  for (let i = 0; i < n; i++) {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    out.push(20 + (h % 75)); // 20-95 %
  }
  _WAVE_CACHE.set(key, out);
  return out;
}


export default function ProductionSuite() {
  const { user } = useAuth();
  const stationName = user?.station_name || "Your station";

  // ── Transport state ──────────────────────────────────────
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [bpm, setBpm] = useState(98);
  const [snap, setSnap] = useState("1/4");
  const [musicKey, setMusicKey] = useState("F maj");

  // ── Tracks / clips / volumes / fx ───────────────────────
  const [tracks, setTracks] = useState(DEFAULT_TRACKS);
  const [clips, setClips] = useState(DEFAULT_CLIPS);
  const [volumes, setVolumes] = useState(DEFAULT_VOLUMES);
  const [fx, setFx] = useState(DEFAULT_FX);
  const [mute, setMute] = useState({});
  const [solo, setSolo] = useState({});
  const [selectedTrackId, setSelectedTrackId] = useState("vo");
  const [selectedClipId, setSelectedClipId] = useState(null);

  // Update #155 — drag-to-reorder tracks. Grab the leftmost color-bar
  // grip on any track header and drag up/down to reorder. Uses direct
  // DOM manipulation (no React re-renders) during the drag so it stays
  // 60fps, consistent with the clip drag physics.
  const {
    reorderingId,
    onGripPointerDown,
    dropLineRef,
    trackLabelsRef,
  } = usePSTrackReorder({ tracks, setTracks });

  // Timeline duration grows dynamically to fit the longest clip so
  // producers can trim a 2-3 minute imported track from its right edge.
  // Before this hoist, the timeline was hard-capped at 30 s and any
  // longer clip's right-edge trim handle was off-screen and unreachable.
  const projectDuration = useMemo(
    () => Math.max(
      MIN_PROJECT_DURATION,
      Math.ceil(clips.reduce((m, c) => Math.max(m, (c.start || 0) + (c.duration || 0)), 0)) + TIMELINE_TAIL_BUFFER,
    ),
    [clips],
  );
  // Ref so the play-loop's setInterval can see the current value without
  // re-subscribing every time the duration grows.
  const projectDurationRef = useRef(projectDuration);
  useEffect(() => { projectDurationRef.current = projectDuration; }, [projectDuration]);

  // ── Timeline zoom (Ctrl +/-/0 + toolbar buttons) ──────────
  // Persisted per-user so producers who prefer a compressed overview
  // keep it between sessions.
  const [pixelsPerSecond, setPixelsPerSecond] = useState(() => {
    try {
      const raw = localStorage.getItem(ZOOM_STORAGE_KEY);
      const n = parseInt(raw || "", 10);
      return ZOOM_LEVELS.includes(n) ? n : DEFAULT_ZOOM;
    } catch { return DEFAULT_ZOOM; }
  });
  useEffect(() => {
    try { localStorage.setItem(ZOOM_STORAGE_KEY, String(pixelsPerSecond)); } catch { /* noop */ }
  }, [pixelsPerSecond]);
  const zoomIndex = ZOOM_LEVELS.indexOf(pixelsPerSecond);
  const canZoomIn  = zoomIndex < ZOOM_LEVELS.length - 1;
  const canZoomOut = zoomIndex > 0;
  const zoomIn  = useCallback(() => setPixelsPerSecond((v) => {
    const i = ZOOM_LEVELS.indexOf(v);
    return i < ZOOM_LEVELS.length - 1 ? ZOOM_LEVELS[i + 1] : v;
  }), []);
  const zoomOut = useCallback(() => setPixelsPerSecond((v) => {
    const i = ZOOM_LEVELS.indexOf(v);
    return i > 0 ? ZOOM_LEVELS[i - 1] : v;
  }), []);
  const zoomReset = useCallback(() => setPixelsPerSecond(DEFAULT_ZOOM), []);
  // DOM ref for the scrollable timeline element — used by "Fit" to
  // measure the visible viewport width so it can pick the highest
  // zoom level that still lets the whole project fit on screen.
  const timelineScrollerRef = useRef(null);
  // Update #149 — DOM ref for the snap-indicator line. Written to
  // directly from the drag rAF loop so no React re-render is needed
  // to show the pulse.
  const snapIndicatorRef = useRef(null);
  const zoomFit = useCallback(() => {
    const viewport = timelineScrollerRef.current?.clientWidth || 0;
    if (viewport <= 0) return;
    const dur = projectDurationRef.current || MIN_PROJECT_DURATION;
    const target = viewport / dur;   // pixels-per-second that would fit exactly
    // Pick the highest ZOOM_LEVEL that is ≤ target — that's the tightest
    // fit that still leaves everything on screen. Fall back to the
    // minimum zoom if even that doesn't fit.
    const chosen = [...ZOOM_LEVELS].reverse().find((z) => z <= target) || ZOOM_LEVELS[0];
    setPixelsPerSecond(chosen);
  }, []);
  // Ref for callbacks that need the current zoom without re-binding.
  const zoomRef = useRef(pixelsPerSecond);
  useEffect(() => { zoomRef.current = pixelsPerSecond; }, [pixelsPerSecond]);

  useEffect(() => {
    // Ctrl/Cmd + = / + → zoom in
    // Ctrl/Cmd + -     → zoom out
    // Ctrl/Cmd + 0     → reset
    // Skip when a text input / textarea has focus so we don't hijack
    // the browser's own zoom or typing.
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const tag = (e.target?.tagName || "").toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      if (e.key === "=" || e.key === "+") { e.preventDefault(); zoomIn(); }
      else if (e.key === "-") { e.preventDefault(); zoomOut(); }
      else if (e.key === "0") { e.preventDefault(); zoomReset(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomIn, zoomOut, zoomReset]);

  // ── Project / UI state ──────────────────────────────────
  const [projectName, setProjectName] = useState("Untitled session");
  const [showProjects, setShowProjects] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showStemModal, setShowStemModal] = useState(false);
  const [importTab, setImportTab] = useState("upload");
  const [aiMixing, setAiMixing] = useState(false);
  // Update #153 — track header context menu: { trackId, x, y } | null
  const [trackMenu, setTrackMenu] = useState(null);
  // Update #154 — the shortcuts card is opt-in only.
  //
  // Previously we auto-opened the coach modal on first visit, but the
  // full-viewport backdrop covered the tracks + timeline before the
  // user had a chance to see them ("Where are the track listings?").
  // Now the DAW is always visible on load; users can pop the help
  // anytime via the Shortcuts button in the header or by pressing `?`.
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Update #158 — sticky mini-transport. When the real transport bar
  // (Zone 2) scrolls out of view we reveal a slim floating strip at the
  // top of the viewport with Play / Stop / time / BPM so producers can
  // still control the transport even when they're deep in the Asset
  // Library or Import drawer.
  const transportRef = useRef(null);
  const [miniTransportVisible, setMiniTransportVisible] = useState(false);
  useEffect(() => {
    const el = transportRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setMiniTransportVisible(!entry.isIntersecting),
      // -60px top margin so the mini appears *before* the real bar is
      // fully gone — no flicker on the boundary.
      { root: null, threshold: 0, rootMargin: "-60px 0px 0px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Shared Stop handler — used by both the real transport and the sticky
  // mini so users can bail out from either.
  const handleStop = useCallback(() => {
    setPlaying(false);
    setPosition(0);
    playerRef.current?.stop();
    try { window.dispatchEvent(new CustomEvent("bfa:ps-stop-all-preview")); } catch { /* noop */ }
  }, []);

  // Update #162 — deep-link entry from the Commercials page. When the
  // Production Suite opens with `?highlight=<assetId>`, PSAssetLibrary
  // switches to the Uploads tab and pulses that card so producers can
  // drag their commercial voice onto any track in one gesture.
  const highlightAssetId = React.useMemo(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("highlight");
  }, []);

  // Update #163 — return-trip breadcrumb. When PS was opened via the
  // Commercials page (`?commercial=<commercialId>`), show a "Back to
  // commercial" chip in the top bar so producers aren't stuck in the
  // DAW after mixing bells & whistles around the ad voice.
  const navigate = useNavigate();
  const backToCommercialId = React.useMemo(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("commercial");
  }, []);
  const [backToCommercial, setBackToCommercial] = useState(null);
  useEffect(() => {
    if (!backToCommercialId) return;
    let cancelled = false;
    api.get(`/commercials/${backToCommercialId}`).then((r) => {
      if (!cancelled) setBackToCommercial(r.data);
    }).catch(() => { /* silent — button just falls back to a generic label */ });
    return () => { cancelled = true; };
  }, [backToCommercialId]);

  // ── Playhead loop ────────────────────────────────────────
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => {
      setPosition((p) => {
        if (p >= projectDurationRef.current) { setPlaying(false); return 0; }
        return p + 0.1;
      });
    }, 100);
    return () => clearInterval(id);
  }, [playing]);

  // ── Keyboard shortcuts ───────────────────────────────────
  // Space → toggle master transport play/pause
  // J     → rewind 5 s (also doubles as DAW-standard "reverse shuttle")
  // K     → pause + hold (DAW shuttle convention)
  // L     → play forward (DAW shuttle convention)
  // Home  → skip to start
  // M     → toggle mute on currently selected track
  // S     → toggle solo on currently selected track
  // ?     → toggle the keyboard-shortcuts help overlay
  // Esc   → close any open modal AND broadcast a stop-all-preview event
  //
  // All shortcuts are skipped when the user is typing into an input/textarea
  // or a contenteditable region, and when modifier keys are held (so OS-
  // level shortcuts like Cmd+Space / Ctrl+L stay intact).
  useEffect(() => {
    const isTypingTarget = (el) => {
      if (!el || el === document.body) return false;
      const tag = (el.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      if (el.isContentEditable) return true;
      return false;
    };
    const onKey = (e) => {
      if (isTypingTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const code = e.code;
      const k = e.key;
      if (code === "Space") {
        e.preventDefault();
        setPlaying((p) => !p);
        return;
      }
      if (k === "Escape") {
        try { window.dispatchEvent(new CustomEvent("bfa:ps-stop-all-preview")); } catch { /* noop */ }
        if (showShortcuts) { setShowShortcuts(false); return; }
        if (showImport) { setShowImport(false); return; }
        if (showExport) { setShowExport(false); return; }
        if (showProjects) { setShowProjects(false); return; }
        if (showStemModal) { setShowStemModal(false); return; }
        if (trackMenu) { setTrackMenu(null); return; }
        return;
      }
      // Ignore the rest while any modal is open — they'd interact with the
      // page underneath which would be confusing.
      if (showImport || showExport || showProjects || showShortcuts || showStemModal || trackMenu) return;

      if (k === "?" || (e.shiftKey && k === "/")) {
        e.preventDefault();
        setShowShortcuts((v) => !v);
        return;
      }
      if (k === "j" || k === "J") {
        e.preventDefault();
        setPosition((p) => Math.max(0, p - 5));
        return;
      }
      if (k === "k" || k === "K") {
        e.preventDefault();
        setPlaying(false);
        return;
      }
      if (k === "l" || k === "L") {
        e.preventDefault();
        setPlaying(true);
        return;
      }
      if (k === "Home") {
        e.preventDefault();
        setPosition(0);
        return;
      }
      if (k === "m" || k === "M") {
        e.preventDefault();
        if (selectedTrackId) {
          setMute((mm) => ({ ...mm, [selectedTrackId]: !mm[selectedTrackId] }));
        }
        return;
      }
      if (k === "s" || k === "S") {
        e.preventDefault();
        if (selectedTrackId) {
          setSolo((ss) => ({ ...ss, [selectedTrackId]: !ss[selectedTrackId] }));
        }
        return;
      }
      // Delete / Backspace — remove the currently selected clip from the timeline
      if (k === "Delete" || k === "Backspace") {
        if (selectedClipId) {
          e.preventDefault();
          setClips((cs) => cs.filter((c) => c.id !== selectedClipId));
          setSelectedClipId(null);
        }
        return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showImport, showExport, showProjects, showShortcuts, showStemModal, trackMenu, selectedTrackId, selectedClipId]);

  // ── Audio playback engine ────────────────────────────────
  // Drives both real-audio clips (audioUrl) and synthesised seed clips so the
  // transport Play button is actually audible. Honors mute/solo + faders.
  const playerRef = useRef(null);
  if (!playerRef.current) playerRef.current = new TransportPlayer();
  const playerStateRef = useRef({});
  playerStateRef.current = { clips, tracks, volumes, mute, solo, currentTime: position };
  useEffect(() => {
    playerStateRef.current = { clips, tracks, volumes, mute, solo, currentTime: position };
  }, [clips, tracks, volumes, mute, solo, position]);
  useEffect(() => {
    if (playing) {
      playerRef.current.start(() => playerStateRef.current, position);
    } else {
      playerRef.current.pause();
    }
    return () => playerRef.current?.pause();
  }, [playing]);
  // Stop also fully resets playback
  useEffect(() => () => playerRef.current?.stop(), []);

  // ── Auto-save every 2 minutes ───────────────────────────
  const stateRef = useRef({});
  stateRef.current = { projectName, tracks, clips, volumes, fx, bpm, musicKey };
  useEffect(() => {
    const id = setInterval(() => {
      try {
        const projects = JSON.parse(localStorage.getItem("ps_projects") || "[]");
        const next = { ...stateRef.current, savedAt: Date.now() };
        const idx = projects.findIndex((p) => p.name === next.projectName);
        if (idx >= 0) projects[idx] = next; else projects.push(next);
        localStorage.setItem("ps_projects", JSON.stringify(projects));
      } catch { /* localStorage blocked — silent */ }
    }, 120000);
    return () => clearInterval(id);
  }, []);

  // ── Pending clips handoff (Artist Drops → PS, Imaging stems → PS) ──
  // Reads localStorage["ps_pending_clips"] once on mount. Each pending clip can
  // include a `target_track` hint ("vo", "bed", "drums", "bass", "other") so
  // stem-separated assets land on dedicated tracks instead of stacking on VO.
  useEffect(() => {
    let raw;
    try { raw = localStorage.getItem("ps_pending_clips"); } catch { return; }
    if (!raw) return;
    let pending = [];
    try { pending = JSON.parse(raw); } catch { pending = []; }
    if (!Array.isArray(pending) || pending.length === 0) return;

    const STEM_COLOR = { vocals: "#4a9eff", drums: "#ff6b6b", bass: "#a78bfa", other: "#3ecf8e" };
    const STEM_TRACK_NAME = { vocals: "Vocals", drums: "Drums", bass: "Bass", other: "Other" };
    const STEM_TRACK_ID = { vocals: "vo", drums: "drums", bass: "bass", other: "other" };

    setTracks((prevTracks) => {
      const have = new Set(prevTracks.map((t) => t.id));
      const newTracks = [];
      pending.forEach((p) => {
        const stem = (p.stem_name || "").toLowerCase();
        if (stem && STEM_TRACK_ID[stem] && stem !== "vocals") {
          const tid = STEM_TRACK_ID[stem];
          if (!have.has(tid)) {
            newTracks.push({
              id: tid,
              name: STEM_TRACK_NAME[stem] || stem,
              type: "Audio · stereo",
              color: STEM_COLOR[stem] || "#a9b3c9",
            });
            have.add(tid);
          }
        }
      });
      if (newTracks.length === 0) return prevTracks;
      // Seed volumes for the new tracks
      setVolumes((v) => {
        const copy = { ...v };
        newTracks.forEach((t) => { copy[t.id] = 80; });
        return copy;
      });
      return [...prevTracks, ...newTracks];
    });

    setClips((prev) => {
      // Track-aware stacking: each target track gets its own running cursor.
      const cursors = {};
      prev.forEach((c) => {
        const end = (c.start || 0) + (c.duration || 0);
        cursors[c.track] = Math.max(cursors[c.track] || 0, end);
      });
      const additions = pending.map((p, i) => {
        const stem = (p.stem_name || "").toLowerCase();
        const targetTrack = p.target_track || (stem ? STEM_TRACK_ID[stem] || "vo" : "vo");
        const dur = Math.max(0.5, parseFloat(p.duration_sec) || 4);
        const cur = cursors[targetTrack] || 0;
        const start = cur + (cur === 0 ? 0.2 : 0.1);
        cursors[targetTrack] = start + dur;
        return {
          id: `c_${Date.now().toString(36)}_${i}`,
          track: targetTrack,
          name: p.name || (stem ? `${STEM_TRACK_NAME[stem] || stem} stem` : "Imported clip"),
          start, duration: dur,
          color: p.color || STEM_COLOR[stem] || "#4a9eff",
          assetId: p.asset_id || "",
          audioUrl: p.audio_url || "",
          source: p.source || (stem ? "stem" : "artist_drop"),
        };
      });
      return [...prev, ...additions];
    });

    // Focus the first pending clip's track for instant feedback
    const first = pending[0] || {};
    const firstStem = (first.stem_name || "").toLowerCase();
    setSelectedTrackId(first.target_track || STEM_TRACK_ID[firstStem] || "vo");

    try { localStorage.removeItem("ps_pending_clips"); } catch { /* ignore */ }
    const stemCount = pending.filter((p) => p.stem_name).length;
    const dropCount = pending.length - stemCount;
    const parts = [];
    if (stemCount) parts.push(`${stemCount} stem${stemCount === 1 ? "" : "s"}`);
    if (dropCount) parts.push(`${dropCount} clip${dropCount === 1 ? "" : "s"}`);
    toast.success(
      `Added ${parts.join(" + ")} to the timeline`,
      { description: pending.map((p) => p.name).slice(0, 3).join(" · "), duration: 5000 }
    );
  }, []);


  // ── Volume / mute / solo helpers ────────────────────────
  const setVolume = useCallback((id, v) => {
    setVolumes((prev) => ({ ...prev, [id]: v }));
  }, []);
  const toggleMute = (id) => setMute((m) => ({ ...m, [id]: !m[id] }));
  const toggleSolo = (id) => setSolo((m) => ({ ...m, [id]: !m[id] }));

  // ── FX helpers ──────────────────────────────────────────
  const toggleFx = (trackId, idx) => {
    setFx((prev) => {
      const list = (prev[trackId] || []).map((f, i) => i === idx ? { ...f, enabled: !f.enabled } : f);
      return { ...prev, [trackId]: list };
    });
  };
  const addFx = async (trackId) => {
    if (!trackId) return;
    const name = await appPrompt("Effect name", "EQ");
    if (!name) return;
    const value = await appPrompt("Setting (e.g. '+3dB @ 2kHz')", "");
    setFx((prev) => ({
      ...prev,
      [trackId]: [...(prev[trackId] || []),
        { name, value: value || "", color: "#4a9eff", enabled: true }],
    }));
  };

  // ── Track / clip helpers ────────────────────────────────
  const addTrack = async () => {
    const name = await appPrompt("Track name", "New track");
    if (!name) return;
    const id = `t_${Date.now().toString(36)}`;
    const colors = ["#4a9eff", "#3ecf8e", "#a78bfa", "#f59e0b", "#ff6b6b"];
    const color = colors[tracks.length % colors.length];
    setTracks((t) => [...t, { id, name, type: "Audio · stereo", color }]);
    setVolumes((v) => ({ ...v, [id]: 70 }));
    setFx((f) => ({ ...f, [id]: [] }));
  };

  // Update #153 — insert a new track above/below an existing one (via
  // the track-header right-click menu). Same style as the top-level
  // "+ TRACKS" button but positioned in the list.
  const insertTrackAt = async (siblingId, position /* "above" | "below" */) => {
    const name = await appPrompt("Track name", "New track");
    if (!name) return;
    const id = `t_${Date.now().toString(36)}`;
    const colors = ["#4a9eff", "#3ecf8e", "#a78bfa", "#f59e0b", "#ff6b6b"];
    setTracks((prev) => {
      const idx = prev.findIndex((t) => t.id === siblingId);
      if (idx === -1) return [...prev, { id, name, type: "Audio · stereo", color: colors[prev.length % colors.length] }];
      const insertIdx = position === "above" ? idx : idx + 1;
      const color = colors[prev.length % colors.length];
      const next = [...prev];
      next.splice(insertIdx, 0, { id, name, type: "Audio · stereo", color });
      return next;
    });
    setVolumes((v) => ({ ...v, [id]: 70 }));
    setFx((f) => ({ ...f, [id]: [] }));
    setTrackMenu(null);
  };

  const removeTrack = async (trackId) => {
    if (tracks.length <= 1) {
      toast.error("At least one track must remain");
      setTrackMenu(null);
      return;
    }
    const clipsOnTrack = clips.filter((c) => c.track === trackId).length;
    if (clipsOnTrack > 0 && !await appConfirm(
      `Delete this track and its ${clipsOnTrack} clip${clipsOnTrack === 1 ? "" : "s"}?`,
    )) {
      setTrackMenu(null);
      return;
    }
    setTracks((t) => t.filter((tr) => tr.id !== trackId));
    setClips((c) => c.filter((cl) => cl.track !== trackId));
    setVolumes(({ [trackId]: _drop, ...rest }) => rest);
    setFx(({ [trackId]: _fxDrop, ...rest }) => rest);
    setMute(({ [trackId]: _m, ...rest }) => rest);
    setSolo(({ [trackId]: _s, ...rest }) => rest);
    if (selectedTrackId === trackId) setSelectedTrackId(tracks[0].id);
    setTrackMenu(null);
  };

  const onAssetDrop = (e, trackId) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("assetData");
    if (!raw) return;
    let asset;
    try { asset = JSON.parse(raw); } catch { return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const dropX = e.clientX - rect.left + e.currentTarget.scrollLeft;
    const start = Math.max(0, dropX / pixelsPerSecond);
    const duration = parseFloat(asset.duration_sec) || (() => {
      // "0:28" → 28s, "1:02" → 62s
      const parts = (asset.duration || "0:00").split(":");
      return parseInt(parts[0], 10) * 60 + parseInt(parts[1] || "0", 10);
    })() || 5;
    setClips((c) => [...c, {
      id: `c_${Date.now().toString(36)}`,
      track: trackId,
      name: asset.name,
      start, duration,
      assetId: asset.id,
      audioUrl: asset.audio_url || asset.audioUrl || "",
      source: asset.source || asset.type || "library",
      color: asset.color,
    }]);
    toast.success(`Added "${asset.name}" to ${tracks.find((t) => t.id === trackId)?.name || "track"}`);
    // Record recent-use for SFX pack drops so the library's "★ Recent"
    // chip surfaces the user's go-to stingers. Fire-and-forget — a
    // network hiccup here shouldn't block the drop UX.
    if (asset.source === "sfx_pack" && asset.id?.startsWith("sfx_")) {
      const itemId = asset.id.slice(4);
      api.post("/production/sfx/recent", { item_id: itemId })
        .then(() => window.dispatchEvent(new CustomEvent("bfa:sfx-recent-updated")))
        .catch(() => { /* silent — best-effort telemetry */ });
    }
  };

  const onClipMouseDown = (e, clip) => {
    e.stopPropagation();
    // Update #145 — prevent the browser's default text-selection so the
    // cursor doesn't flip to "text" and drop the drag halfway through.
    // Without this, mousedown on the clip name kicks off a native
    // selection and the drag "grips but never moves".
    e.preventDefault();
    setSelectedClipId(clip.id);

    // Detect which grabber the user clicked — left edge / right edge / body.
    // The ClipBlock renders a `data-edge` attr on the two edge handles;
    // fall back to "body" for everything else.
    const edge = e.target?.dataset?.edge || "body";
    const startX = e.clientX;
    const origStart = clip.start;
    const origDuration = clip.duration;
    const MIN_DURATION = 0.25;    // seconds — keep clips clickable

    // Update #147 — bypass React during the drag. Every state update
    // during a drag was forcing a full ProductionSuite render
    // (Timeline + Mixer + FX chain + 400-card Asset Library). Now we
    // mutate the clip's DOM directly via a captured `clipEl`, then
    // commit ONE state update on mouseup. This is what Ableton / Figma
    // do for silky drags on complex pages.
    //
    // `e.currentTarget` is the outer ClipBlock div (thanks to how the
    // React onMouseDown is bound on the outer element). Walk up the
    // parent chain if the user clicked one of the edge grabbers so we
    // always operate on the outer positioned element.
    // Update #152 — cross-track drag support. Enumerate all track rows
    // at mousedown so we can detect vertical hover into another track
    // during the drag. On mouseup we commit both `start` AND `track` in
    // a single setClips.
    const trackRows = Array.from(document.querySelectorAll('[data-testid^="ps-cliprow-"]'))
      .map((el) => ({
        id: el.getAttribute("data-testid").replace("ps-cliprow-", ""),
        el,
        rect: el.getBoundingClientRect(),
      }));
    const origTrackRow = trackRows.find((r) => r.id === clip.track);
    let targetTrackId = clip.track;
    let pointerY = e.clientY;

    let clipEl = e.currentTarget;
    while (clipEl && !clipEl.hasAttribute("data-testid")) clipEl = clipEl.parentElement;
    const origLeft = origStart * zoomRef.current;
    const origWidth = origDuration * zoomRef.current;

    // Update #148 — snap-to-grid during drag.
    // • Snap pill "1/4" → quarter-note grid → 60 / BPM seconds
    // • Snap pill "1/8" → eighth-note grid → 30 / BPM seconds
    // • Snap pill "Free" → free drag (no rounding)
    // Holding SHIFT during the drag inverts the snap state — matches
    // Pro Tools / Ableton convention. Modifier is polled per-move so
    // the user can toggle mid-drag.
    const snapMode = snap;   // captured at mousedown; doesn't change mid-drag
    const beatSec = 60 / Math.max(40, bpm);
    const snapUnitSec =
      snapMode === "1/4" ? beatSec :
      snapMode === "1/8" ? beatSec / 2 :
      0;                    // 0 = free
    let shiftHeld = e.shiftKey;
    const snapSec = (value) => {
      // Effective snap: inverted by shift so users can quickly break out
      // of the grid without touching the transport pills.
      const active = shiftHeld ? (snapUnitSec === 0 ? beatSec : 0) : snapUnitSec;
      if (active <= 0) return value;
      return Math.round(value / active) * active;
    };
    // Update #149 — pulse a subtle vertical line whenever the snapped
    // value changes so the user gets confirmation the clip "clicked in"
    // to the grid. We track the last snapped seconds; on change we
    // reposition the indicator + toggle a CSS class for the animation.
    let lastSnappedSec = -1;
    let snapClearTimer = 0;
    const flashSnap = (snappedSec) => {
      if (snappedSec === lastSnappedSec) return;
      lastSnappedSec = snappedSec;
      const el = snapIndicatorRef.current;
      if (!el) return;
      el.style.left = `${snappedSec * zoomRef.current}px`;
      el.classList.add(s.snapIndicatorPulse);
      if (snapClearTimer) clearTimeout(snapClearTimer);
      snapClearTimer = setTimeout(() => {
        el.classList.remove(s.snapIndicatorPulse);
      }, 220);
    };

    // Update #145 — auto-scroll the timeline while the pointer hugs the
    // viewport edge so the user can drag a clip far past the initially
    // visible area (needed when the timeline is zoomed in on a 3-minute
    // imported track). Runs on a rAF loop until mouseup.
    const scroller = timelineScrollerRef.current;
    const origScrollLeft = scroller ? scroller.scrollLeft : 0;
    const AUTOSCROLL_EDGE = 40;
    const AUTOSCROLL_MAX_STEP = 22;
    let pointerX = e.clientX;
    let rafHandle = 0;
    // Current preview values — DOM-only during the drag, committed to
    // React state on mouseup.
    let previewStart = origStart;
    let previewDuration = origDuration;

    const applyMoveDom = () => {
      // scrollDelta accounts for the auto-scroll: the pointer's
      // effective x in timeline coords is (clientX + scrollLeft).
      const scrollDelta = (scroller?.scrollLeft || 0) - origScrollLeft;
      const dxPx = (pointerX - startX) + scrollDelta;
      const dxSec = dxPx / zoomRef.current;

      if (edge === "left") {
        const maxLeft = origStart + origDuration - MIN_DURATION;
        const newStartRaw = Math.min(Math.max(0, origStart + dxSec), maxLeft);
        const newStart = snapSec(newStartRaw);
        const shift = newStart - origStart;
        const newDuration = Math.max(MIN_DURATION, origDuration - shift);
        previewStart = newStart;
        previewDuration = newDuration;
        if (clipEl) {
          clipEl.style.left = `${newStart * zoomRef.current}px`;
          clipEl.style.width = `${newDuration * zoomRef.current}px`;
        }
        if (newStart !== newStartRaw) flashSnap(newStart);
      } else if (edge === "right") {
        // Snap the END time (start + duration) rather than the duration
        // itself — musicians think in bar lines, not durations.
        const newEndRaw = origStart + Math.max(MIN_DURATION, origDuration + dxSec);
        const newEnd = snapSec(newEndRaw);
        const newDuration = Math.max(MIN_DURATION, newEnd - origStart);
        previewDuration = newDuration;
        if (clipEl) clipEl.style.width = `${newDuration * zoomRef.current}px`;
        if (newEnd !== newEndRaw) flashSnap(newEnd);
      } else {
        const newStartRaw = Math.max(0, origStart + dxSec);
        const newStart = snapSec(newStartRaw);
        previewStart = newStart;

        // Update #152 — while dragging, follow the pointer vertically:
        // find the track row currently under the pointer and translate
        // the clip DOM into it. Only body-drag supports track hopping
        // (edge trims stay on the same track).
        let newTrackId = origTrackRow ? origTrackRow.id : clip.track;
        for (const row of trackRows) {
          if (pointerY >= row.rect.top && pointerY <= row.rect.bottom) {
            newTrackId = row.id;
            break;
          }
        }
        targetTrackId = newTrackId;

        const dyPx = origTrackRow
          ? (trackRows.find((r) => r.id === newTrackId)?.rect.top || origTrackRow.rect.top) - origTrackRow.rect.top
          : 0;

        if (clipEl) {
          clipEl.style.transform =
            `translate(${(newStart - origStart) * zoomRef.current}px, ${dyPx}px)`;
          // Elevate the clip so it visibly floats above sibling rows
          clipEl.style.zIndex = "20";
          clipEl.style.pointerEvents = "none";  // don't intercept the drop row's events
        }
        if (newStart !== newStartRaw) flashSnap(newStart);
      }
    };

    const tickScroll = () => {
      if (scroller) {
        const rect = scroller.getBoundingClientRect();
        const leftDist  = pointerX - rect.left;
        const rightDist = rect.right - pointerX;
        let sdx = 0;
        if (leftDist < AUTOSCROLL_EDGE) {
          sdx = -Math.ceil(((AUTOSCROLL_EDGE - leftDist) / AUTOSCROLL_EDGE) * AUTOSCROLL_MAX_STEP);
        } else if (rightDist < AUTOSCROLL_EDGE) {
          sdx = Math.ceil(((AUTOSCROLL_EDGE - rightDist) / AUTOSCROLL_EDGE) * AUTOSCROLL_MAX_STEP);
        }
        if (sdx !== 0) scroller.scrollLeft = Math.max(0, scroller.scrollLeft + sdx);
      }
      applyMoveDom();
      rafHandle = requestAnimationFrame(tickScroll);
    };

    const onMove = (ev) => {
      pointerX = ev.clientX;
      pointerY = ev.clientY;
      shiftHeld = ev.shiftKey;
    };

    const stop = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", stop);
      if (rafHandle) cancelAnimationFrame(rafHandle);
      if (snapClearTimer) clearTimeout(snapClearTimer);
      if (snapIndicatorRef.current) {
        snapIndicatorRef.current.classList.remove(s.snapIndicatorPulse);
      }
      // Reset the DOM-only transform, then commit the final position
      // to React state so the timeline width + all downstream memos
      // (project duration, playhead, etc.) recompute exactly once.
      if (clipEl) {
        clipEl.style.transform = "";
        clipEl.style.zIndex = "";
        clipEl.style.pointerEvents = "";
        clipEl.style.left = `${previewStart * zoomRef.current}px`;
        clipEl.style.width = `${previewDuration * zoomRef.current}px`;
      }
      // Update #152 — commit both start/duration AND (for body drags) the
      // new track ID. React re-parents the clip into the target row on
      // its next render.
      setClips((cs) => cs.map((c) => (
        c.id === clip.id
          ? {
              ...c,
              start: previewStart,
              duration: previewDuration,
              track: edge === "body" ? targetTrackId : c.track,
            }
          : c
      )));
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", stop);
    rafHandle = requestAnimationFrame(tickScroll);

    // Force the global cursor to match the gesture so the user always sees
    // a "grabbing" (or "ew-resize" for edges) cursor even when the pointer
    // leaves the clip during a fast drag. Restored on mouseup.
    const priorCursor = document.body.style.cursor;
    document.body.style.cursor = edge === "body" ? "grabbing" : "ew-resize";
    const restoreCursor = () => { document.body.style.cursor = priorCursor; };
    document.addEventListener("mouseup", restoreCursor, { once: true });
  };

  // ── AI Mix ──────────────────────────────────────────────
  const handleAIMix = async () => {
    setAiMixing(true);
    try {
      const r = await api.post("/production/ai-mix", {
        tracks: tracks.map((t) => ({ id: t.id, name: t.name, type: t.type })),
        clips: clips.map((c) => ({ track: c.track, name: c.name, start: c.start, duration: c.duration })),
        volumes,
        bpm,
        key_signature: musicKey,
      });
      const { adjustments = [], summary, source } = r.data || {};
      // Apply with smooth transitions — CSS transition on faderFill height
      adjustments.forEach((adj) => setVolume(adj.trackId, adj.volume));
      toast.success(
        `AI mix · ${adjustments.length} adjustment${adjustments.length === 1 ? "" : "s"}`
        + (summary ? ` — ${summary}` : "")
        + (source === "fallback" ? " (rule-based — Claude unavailable)" : ""),
        { duration: 6000 }
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI Mix failed");
    } finally { setAiMixing(false); }
  };

  const handlePushToAir = async () => {
    if (!await appConfirm(`Push "${projectName}" to ${stationName}'s automation system?`)) return;
    try {
      const r = await api.post("/production/push-to-air", {
        project_name: projectName,
        duration_sec: projectDuration,
      });
      toast.success(r.data.message || "Pushed to air");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Push failed");
    }
  };

  const [stemBusy, setStemBusy] = useState(false);

  // Opens the mode-picker modal. Actual separation is dispatched from
  // runStemSeparation once the user chooses standard (4) vs advanced (10).
  const handleStemSeparate = () => {
    if (!selectedClipId) { toast.message("Select a clip in the timeline first"); return; }
    const clip = clips.find((c) => c.id === selectedClipId);
    if (!clip) { toast.message("Selected clip not found"); return; }
    const assetId = clip.assetId || clip.asset_id || "";
    if (!assetId) { toast.error("This clip has no source asset — drag in audio from the library first."); return; }
    setShowStemModal(true);
  };

  const runStemSeparation = async (advanced) => {
    if (!selectedClipId) { setShowStemModal(false); return; }
    const clip = clips.find((c) => c.id === selectedClipId);
    if (!clip) { setShowStemModal(false); return; }
    const assetId = clip.assetId || clip.asset_id || "";
    if (!assetId) { setShowStemModal(false); return; }

    setStemBusy(true);
    setShowStemModal(false);
    try {
      const r = await api.post("/production/stem-separate", {
        asset_id: assetId,
        file_name: clip.name || "stem.wav",
        advanced: !!advanced,
      });
      const data = r.data || {};
      if (data.stub) {
        toast.message(data.message || "Connect LALAL.AI or Replicate to use stem separation");
        setStemBusy(false);
        return;
      }

      // If the prediction completed inside the sync path, stems are ready immediately.
      if (data.status === "succeeded" && Array.isArray(data.stems) && data.stems.length > 0) {
        dropStemsOnTracks(data.stems, clip);
        const providerLabel = data.provider === "lalal_ai" ? "LALAL.AI" : "Demucs (Replicate)";
        toast.success(`Stems ready via ${providerLabel} — ${data.stems.length} added to new tracks`);
        setStemBusy(false);
        return;
      }

      // Otherwise poll (Replicate async path)
      const providerHint = data.provider === "lalal_ai" ? "LALAL.AI" : "Demucs";
      toast.message(`Splitting "${clip.name}" — ${providerHint} is processing…`, { duration: 4000 });
      const jobId = data.job_id;
      const started = Date.now();
      const poll = async () => {
        try {
          const s = await api.get(`/production/stem-separate/${jobId}`);
          const j = s.data || {};
          if (j.status === "succeeded" && Array.isArray(j.stems) && j.stems.length > 0) {
            dropStemsOnTracks(j.stems, clip);
            const pLabel = j.provider === "lalal_ai" ? "LALAL.AI" : "Demucs (Replicate)";
            toast.success(`Stems ready via ${pLabel} — ${j.stems.length} added`);
            setStemBusy(false);
            return;
          }
          if (j.status === "failed") {
            toast.error(`Stem separation failed: ${j.error || "unknown error"}`);
            setStemBusy(false);
            return;
          }
          if (Date.now() - started > 5 * 60 * 1000) {
            toast.error("Stem separation timed out after 5 minutes");
            setStemBusy(false);
            return;
          }
          setTimeout(poll, 3000);
        } catch (e) {
          toast.error("Polling failed — try again");
          setStemBusy(false);
        }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "byok_required") {
        toast.error("Connect LALAL.AI (primary) or Replicate (fallback) in AI Provider Center to use stem separation", {
          duration: 6000,
          action: { label: "Open", onClick: () => window.open("/dashboard/providers", "_blank") },
        });
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Stem separation failed"));
      }
      setStemBusy(false);
    }
  };

  const dropStemsOnTracks = (stems, sourceClip) => {
    const stemColor = {
      vocals: "#4a9eff", drums: "#ff6b6b", bass: "#a78bfa", other: "#3ecf8e",
      piano: "#f472b6", electric_guitar: "#fb923c", acoustic_guitar: "#facc15",
      synthesizer: "#a78bfa", strings: "#22d3ee", wind: "#84cc16",
    };
    setTracks((prev) => {
      const existingIds = new Set(prev.map((t) => t.id));
      const newTracks = stems.map((stem) => {
        const id = `t_stem_${stem.name}_${Date.now().toString(36)}`;
        const pretty = stem.name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        return existingIds.has(id) ? null : {
          id, name: `${pretty} stem`,
          type: "Audio · stereo", color: stemColor[stem.name] || "#a9b3c9",
        };
      }).filter(Boolean);
      // Also seed volumes for new tracks
      setVolumes((v) => {
        const copy = { ...v };
        newTracks.forEach((t) => { copy[t.id] = 80; });
        return copy;
      });
      // And drop the clip on the new track
      setClips((cs) => {
        const additions = newTracks.map((t, i) => ({
          id: `c_stem_${Date.now().toString(36)}_${i}`,
          track: t.id,
          name: stems[i].name.charAt(0).toUpperCase() + stems[i].name.slice(1),
          start: sourceClip.start || 0,
          duration: sourceClip.duration || 0,
          color: t.color,
          assetId: stems[i].asset_id || "",
          audioUrl: stems[i].audio_url || "",
          source: "stem",
        }));
        return [...cs, ...additions];
      });
      return [...prev, ...newTracks];
    });
    setStemBusy(false);
  };

  // ── Selected track for FX chain ─────────────────────────
  const selectedTrack = tracks.find((t) => t.id === selectedTrackId) || tracks[0];

  // ── Render ──────────────────────────────────────────────
  const timelineWidth = projectDuration * pixelsPerSecond;
  const playheadPct = (position / projectDuration) * 100;

  return (
    <>
      {/* Small viewport fallback */}
      <div className={s.tooSmall}>
        <h2>Production Suite works best on a larger screen</h2>
        <p>
          This DAW-style multitrack workspace needs at least 1024px wide.
          Open BroadcastFlow on a desktop to lay down your imaging package.
        </p>
      </div>

      <div className={`${s.psPage} ${s.desktopOnly}`} data-testid="production-suite-page" data-theme="dark">
        {/* Update #158 — sticky mini-transport. Appears when the main
            transport bar scrolls out of view, so Play/Stop/BPM stay
            reachable from anywhere on the page (Asset Library, mixer,
            uploads grid). Uses position:fixed so it lives on top of the
            page-level scroll and never fights the internal timeline
            scroller. */}
        {miniTransportVisible && (
          <div className={s.miniTransport} data-testid="ps-mini-transport" role="toolbar" aria-label="Transport controls">
            <div className={s.miniTransportTime} data-testid="ps-mini-time">{fmt(position)}</div>
            <button
              type="button"
              className={`${s.transportBtn} ${playing ? s.transportBtnActive : ""}`}
              onClick={() => setPlaying((p) => !p)}
              title={`${playing ? "Pause" : "Play"} (Space)`}
              data-testid="ps-mini-play"
            >
              {playing ? <Pause size={12} /> : <Play size={12} />}
            </button>
            <button
              type="button"
              className={s.transportBtn}
              onClick={handleStop}
              title="Stop (Esc)"
              data-testid="ps-mini-stop"
            >
              <Square size={12} />
            </button>
            <div className={s.miniTransportDivider} />
            <span className={s.tLabel}>BPM</span>
            <span className={s.tValue} data-testid="ps-mini-bpm">{bpm}</span>
            <div className={s.miniTransportDivider} />
            <button
              type="button"
              className={s.miniTransportBackBtn}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              title="Scroll back to the main transport"
              data-testid="ps-mini-back-to-top"
            >
              ↑ Back to top
            </button>
          </div>
        )}

        {/* ── ZONE 1: TOP BAR ─────────────────────────────────── */}
        <div className={s.topBar} data-testid="ps-topbar">
          <div className={s.iconSquare}><AudioWaveform size={16} /></div>
          <div>
            <div className={s.topTitle}>Production suite</div>
            <div className={s.topSubtitle}>{stationName} · {projectName}</div>
          </div>
          {/* Update #163 — back-to-commercial breadcrumb. Renders only
              when the DAW was deep-linked from a specific commercial;
              gives users a one-click return path instead of having to
              hunt through the sidebar. */}
          {backToCommercialId && (
            <button
              type="button"
              onClick={() => navigate(`/dashboard/commercials?open=${backToCommercialId}`)}
              className={s.backBreadcrumb}
              data-testid="ps-back-to-commercial"
              title="Return to the Commercial that opened this DAW"
            >
              <ArrowLeft size={11} />
              <Megaphone size={11} />
              <span className={s.backBreadcrumbLabel}>
                Back to {backToCommercial?.advertiser ? `“${backToCommercial.advertiser}”` : "commercial"}
              </span>
            </button>
          )}
          <div className={s.topActions}>
            <button
              type="button"
              className={s.btnSmall}
              onClick={() => setShowShortcuts(true)}
              data-testid="ps-btn-shortcuts"
              title="Keyboard shortcuts (?)"
            >
              <Keyboard size={11} /> Shortcuts
            </button>
            <button type="button" className={s.btnSmall} onClick={() => setShowProjects(true)} data-testid="ps-btn-projects">
              <FolderOpen size={11} /> Projects
            </button>
            <button type="button" className={s.btnSmall} onClick={() => setShowExport(true)} data-testid="ps-btn-export">
              <Download size={11} /> Export
            </button>
            <button type="button" className={s.btnSmall} onClick={handlePushToAir} data-testid="ps-btn-push">
              <Send size={11} /> Push to air
            </button>
            <button
              type="button"
              className={`${s.btnSmall} ${s.btnPrimary}`}
              onClick={handleAIMix}
              disabled={aiMixing}
              data-testid="ps-btn-ai-mix"
            >
              <Sparkles size={11} /> {aiMixing ? "Mixing…" : "AI mix"}
            </button>
          </div>
        </div>

        {/* ── ZONE 2: TRANSPORT BAR ───────────────────────────── */}
        <div className={s.transport} data-testid="ps-transport" ref={transportRef}>
          <div className={s.timeDisplay} data-testid="ps-time">{fmt(position)}</div>
          <button type="button" className={s.transportBtn} onClick={() => setPosition(0)} title="Skip to start" data-testid="ps-skip-start">
            <SkipBack size={13} />
          </button>
          <button type="button" className={s.transportBtn} onClick={() => setPosition((p) => Math.max(0, p - 5))} title="Rewind" data-testid="ps-rewind">
            <SkipBack size={13} />
          </button>
          <button
            type="button"
            className={`${s.transportBtn} ${playing ? s.transportBtnActive : ""}`}
            onClick={() => setPlaying((p) => !p)}
            title={`${playing ? "Pause" : "Play"} (Space)`}
            data-testid="ps-play"
          >
            {playing ? <Pause size={13} /> : <Play size={13} />}
          </button>
          <button type="button" className={s.transportBtn} onClick={handleStop} title="Stop (Esc)" data-testid="ps-stop">
            <Square size={13} />
          </button>
          <button type="button" className={`${s.transportBtn} ${s.transportBtnRec} ${s.transportBtnRecActive}`} title="Record" data-testid="ps-record">
            <Circle size={11} fill="currentColor" />
          </button>

          <div className={s.divider} />
          <span className={s.tLabel}>BPM</span>
          <span className={s.tValue} onClick={async () => {
            const next = await appPrompt("BPM", String(bpm));
            const n = parseInt(next, 10);
            if (n && n >= 40 && n <= 240) setBpm(n);
          }} data-testid="ps-bpm">{bpm}</span>

          <div className={s.divider} />
          {["1/4", "1/8", "Free"].map((sp) => (
            <button
              key={sp} type="button"
              onClick={() => setSnap(sp)}
              className={`${s.snapPill} ${snap === sp ? s.snapPillActive : ""}`}
              data-testid={`ps-snap-${sp.replace("/", "-")}`}
              title={sp === "Free"
                ? "Free drag — no grid snap (hold Shift while dragging to snap to 1/4)"
                : `Snap clip drags to ${sp} notes at ${bpm} BPM (hold Shift while dragging to disable temporarily)`}
            >
              {sp}
            </button>
          ))}

          <div className={s.divider} />
          <span className={s.tLabel}>Key</span>
          <span className={`${s.tValue} ${s.tValueKey}`} onClick={async () => {
            const next = await appPrompt("Key", musicKey);
            if (next) setMusicKey(next);
          }} data-testid="ps-key">{musicKey}</span>

          <div className={s.flexSpacer} />

          <button type="button" className={s.btnSmall} onClick={handleStemSeparate} disabled={stemBusy} data-testid="ps-stem-separate">
            <Layers3 size={11} /> {stemBusy ? "Splitting…" : "Stem separate"}
          </button>
          <button type="button" className={s.btnSmall} data-testid="ps-trim">
            <Scissors size={11} /> Trim
          </button>
          <button type="button" className={s.btnSmall} data-testid="ps-clone" onClick={() => {
            if (!selectedClipId) { toast.message("Select a clip to clone first"); return; }
            const orig = clips.find((c) => c.id === selectedClipId);
            if (!orig) return;
            setClips((cs) => [...cs, { ...orig, id: `c_${Date.now().toString(36)}`, start: orig.start + orig.duration + 0.1 }]);
            toast.success("Clip cloned");
          }}>
            <Copy size={11} /> Clone
          </button>
          <button
            type="button"
            className={s.btnSmall}
            data-testid="ps-delete-clip"
            onClick={() => {
              if (!selectedClipId) {
                toast.message("Select a clip on the timeline first — then click Delete (or press Del / Backspace)");
                return;
              }
              const orig = clips.find((c) => c.id === selectedClipId);
              setClips((cs) => cs.filter((c) => c.id !== selectedClipId));
              setSelectedClipId(null);
              toast.success(`Deleted${orig?.name ? ` "${orig.name}"` : " clip"}`);
            }}
            title="Delete the selected clip (Del / Backspace)"
          >
            <Trash2 size={11} /> Delete
          </button>
          <button
            type="button"
            className={s.btnSmall}
            data-testid="ps-clear-all-clips"
            onClick={async () => {
              if (clips.length === 0) { toast.message("Timeline is already empty"); return; }
              if (!(await appConfirm(`Clear all ${clips.length} clip${clips.length === 1 ? "" : "s"} from the timeline? Tracks are kept.`))) return;
              setClips([]);
              setSelectedClipId(null);
              toast.success(`Cleared ${clips.length} clip${clips.length === 1 ? "" : "s"}`);
            }}
            title="Remove every clip from the timeline (tracks are kept)"
          >
            <Trash2 size={11} /> Clear all
          </button>

          {/* Zoom group — compact, matches the transport button style but
              visually grouped so it reads as a single control cluster. */}
          <div className={s.zoomGroup} data-testid="ps-zoom-group" title="Zoom timeline (Ctrl + / − / 0)">
            <button
              type="button"
              className={s.zoomBtn}
              onClick={zoomOut}
              disabled={!canZoomOut}
              data-testid="ps-zoom-out"
              title="Zoom out (Ctrl −)"
              aria-label="Zoom out"
            >
              <ZoomOut size={11} />
            </button>
            <button
              type="button"
              className={s.zoomLevelBtn}
              onClick={zoomReset}
              data-testid="ps-zoom-reset"
              title="Reset zoom (Ctrl 0)"
            >
              {pixelsPerSecond}px/s
            </button>
            <button
              type="button"
              className={s.zoomBtn}
              onClick={zoomIn}
              disabled={!canZoomIn}
              data-testid="ps-zoom-in"
              title="Zoom in (Ctrl +)"
              aria-label="Zoom in"
            >
              <ZoomIn size={11} />
            </button>
            <button
              type="button"
              className={s.zoomBtn}
              onClick={zoomFit}
              data-testid="ps-zoom-fit"
              title="Fit the whole project to the timeline width"
              aria-label="Fit to window"
            >
              <Maximize2 size={11} />
            </button>
          </div>
        </div>

        {/* ── ZONE 3: MAIN AREA (tracks + timeline) ──────────── */}
        <div className={s.mainArea}>
          <PSTrackHeaders
            tracks={tracks}
            selectedTrackId={selectedTrackId}
            onSelectTrack={setSelectedTrackId}
            mute={mute}
            solo={solo}
            onToggleMute={toggleMute}
            onToggleSolo={toggleSolo}
            onAddTrack={addTrack}
            onInsertTrackAt={insertTrackAt}
            onRemoveTrack={removeTrack}
            reorderingId={reorderingId}
            onGripPointerDown={onGripPointerDown}
            dropLineRef={dropLineRef}
            trackLabelsRef={trackLabelsRef}
            trackMenu={trackMenu}
            onOpenTrackMenu={setTrackMenu}
            onCloseTrackMenu={() => setTrackMenu(null)}
          />

          {/* Timeline */}
          <div className={s.timeline} data-testid="ps-timeline" ref={timelineScrollerRef}>
            <div className={s.timelineInner} style={{ width: timelineWidth }}>
              {/* Ruler */}
              <div
                className={s.ruler}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const pct = (e.clientX - rect.left) / rect.width;
                  setPosition(Math.max(0, Math.min(projectDuration, pct * projectDuration)));
                }}
                data-testid="ps-ruler"
              >
                {Array.from({ length: Math.floor(projectDuration / 5) + 1 }, (_, i) => {
                  const sec = i * 5;
                  const label = `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
                  return (
                    <React.Fragment key={i}>
                      <div className={s.rulerTick} style={{ left: `${(sec / projectDuration) * 100}%` }} />
                      <div className={s.rulerMark} style={{ left: `${(sec / projectDuration) * 100}%` }}>{label}</div>
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Playhead */}
              <div className={s.playhead} style={{ left: `${playheadPct}%` }}>
                <div className={s.playheadTriangle} />
              </div>

              {/* Update #149 — snap indicator: a subtle vertical line that
                  flashes on the timeline when a drag lands on a grid tick.
                  Kept as a DOM-only element (no React re-render) so it
                  can be shown/hidden 60 times a second from the drag rAF
                  loop without any perf cost. */}
              <div
                ref={snapIndicatorRef}
                className={s.snapIndicator}
                data-testid="ps-snap-indicator"
                aria-hidden
              />

              {/* Clip rows */}
              {tracks.map((t) => {
                const rowClips = clips.filter((c) => c.track === t.id);
                return (
                  <div
                    key={t.id}
                    className={s.clipRow}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
                    onDrop={(e) => onAssetDrop(e, t.id)}
                    data-testid={`ps-cliprow-${t.id}`}
                  >
                    {rowClips.map((c) => {
                      const isUnderPlayhead =
                        playing && position >= c.start && position < c.start + c.duration;
                      const anySolo = Object.values(solo).some(Boolean);
                      const trackMuted = !!mute[t.id] || (anySolo && !solo[t.id]);
                      return (
                        <ClipBlock
                          key={c.id}
                          clip={c}
                          track={t}
                          selected={selectedClipId === c.id}
                          playing={isUnderPlayhead && !trackMuted}
                          onMouseDown={(e) => onClipMouseDown(e, c)}
                          pixelsPerSecond={pixelsPerSecond}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── ZONE 4: MIXER + FX CHAIN ────────────────────────── */}
        <div className={s.bottomPanel}>
          <PSMixer tracks={tracks} volumes={volumes} setVolume={setVolume} />
          <PSFXChain selectedTrack={selectedTrack} fx={fx} toggleFx={toggleFx} addFx={addFx} />
        </div>

        {/* ── ZONE 5: ASSET LIBRARY ──────────────────────────── */}
        <PSAssetLibrary
          onOpenImport={(initialTab) => { setImportTab(initialTab); setShowImport(true); }}
          highlightAssetId={highlightAssetId}
        />

        {/* ── MODALS ─────────────────────────────────────────── */}
        {showProjects && (
          <PSProjectsModal
            currentProject={projectName}
            onLoad={(p) => {
              setProjectName(p.name);
              if (!p.fresh) {
                if (p.tracks) setTracks(p.tracks);
                if (p.clips) setClips(p.clips);
                if (p.volumes) setVolumes(p.volumes);
                if (p.fx) setFx(p.fx);
                if (p.bpm) setBpm(p.bpm);
                if (p.musicKey) setMusicKey(p.musicKey);
              } else {
                // Fresh project: keep defaults but reset clips
                setClips([]);
                setPosition(0);
              }
            }}
            onClose={() => setShowProjects(false)}
          />
        )}
        {showExport && (
          <PSExportModal
            projectName={projectName}
            tracks={tracks}
            clips={clips}
            volumes={volumes}
            muted={mute}
            solo={solo}
            onClose={() => setShowExport(false)}
          />
        )}

        {/* ── STEM SEPARATE MODE PICKER ─────────────────────── */}
        <StemSeparateModal
          open={showStemModal}
          busy={stemBusy}
          sourceLabel={(clips.find((c) => c.id === selectedClipId) || {}).name || ""}
          onClose={() => setShowStemModal(false)}
          onConfirm={runStemSeparation}
        />

        {/* Keyboard shortcuts coach card — auto-opens on first visit,
            reopenable anytime via ? key or the header button */}
        <PSShortcutHelp
          open={showShortcuts}
          onClose={() => setShowShortcuts(false)}
        />

        {/* ── MUSIC IMPORT MODAL ─────────────────────────────── */}
        <PSMusicImportModal
          open={showImport}
          initialTab={importTab}
          tracks={tracks}
          selectedTrackId={selectedTrackId}
          onClose={() => setShowImport(false)}
          onAddClips={(incoming) => {
            // Drop each new asset onto its target track at the next available position.
            setClips((prev) => {
              const next = [...prev];
              for (const c of incoming) {
                const trackId = c.trackId || selectedTrackId || "bed";
                const trackClips = next.filter((cl) => cl.track === trackId);
                const nextStart = trackClips.reduce((m, cl) => Math.max(m, cl.start + cl.duration), 0) + 0.2;
                const track = tracks.find((t) => t.id === trackId);
                next.push({
                  id: `c_${Date.now().toString(36)}_${next.length}`,
                  track: trackId,
                  name: (c.name || "Imported").slice(0, 60),
                  start: nextStart,
                  duration: Math.max(0.5, parseFloat(c.duration_sec) || 4),
                  color: track?.color || "#4a9eff",
                  audioUrl: c.audio_url || "",
                  assetId: c.asset_id || "",
                  source: c.source || "import",
                });
              }
              return next;
            });
            setShowImport(false);
          }}
        />

        {/* ── AI MIX OVERLAY ─────────────────────────────────── */}
        {aiMixing && (
          <div className={s.aiOverlay} data-testid="ps-ai-overlay">
            <div className={s.aiCard}>
              <div className={s.aiWave}>
                {Array.from({ length: 7 }, (_, i) => <span key={i} />)}
              </div>
              <div className={s.aiLabel}>Analyzing tracks…</div>
              <div className={s.aiSubtle}>Claude is rebalancing your mix</div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}


function ClipBlockInner({ clip, track, selected, playing, onMouseDown, pixelsPerSecond }) {
  const left = clip.start * pixelsPerSecond;
  const width = clip.duration * pixelsPerSecond;
  const color = clip.color || track.color;
  const bg = colorWithAlpha(color, 0.12);
  // Update #146 — clamp bar count so a 3-minute clip at high zoom doesn't
  // spawn 2000 divs (which crawls the drag loop). MAX_WAVE_BARS keeps
  // the waveform readable at any zoom.
  const waves = waveHeights(clip.id, Math.min(MAX_WAVE_BARS, Math.max(8, Math.floor(width / 8))));

  // Common style for both edge grabbers — 6 px hit target, always
  // clickable but only visually painted when the clip is selected.
  const edgeStyle = {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: 6,
    cursor: "ew-resize",
    zIndex: 3,
    background: selected ? colorWithAlpha(color, 0.55) : "transparent",
    borderRadius: 2,
  };

  return (
    <div
      className={`${s.clip} ${selected ? s.clipSelected : ""} ${playing ? s.clipPlaying : ""}`}
      style={{ left, width, color, background: bg }}
      onMouseDown={onMouseDown}
      data-testid={`ps-clip-${clip.id}`}
    >
      <div className={s.clipName}>{clip.name}</div>
      <div className={s.clipWave}>
        {waves.map((h, i) => (
          <div key={i} className={s.clipWaveBar} style={{ height: `${h}%` }} />
        ))}
      </div>
      {/* Update #134 — left & right edge grabbers for clip trimming */}
      <div
        data-edge="left"
        data-testid={`ps-clip-${clip.id}-trim-left`}
        title="Drag to trim the clip's start"
        style={{ ...edgeStyle, left: 0 }}
      />
      <div
        data-edge="right"
        data-testid={`ps-clip-${clip.id}-trim-right`}
        title="Drag to trim the clip's end"
        style={{ ...edgeStyle, right: 0 }}
      />
    </div>
  );
}

// Update #146 — memoise ClipBlock so a drag on one clip doesn't force
// every other clip in the timeline to re-render. Custom equality check:
// only re-render when the fields we actually use change.
const ClipBlock = React.memo(ClipBlockInner, (prev, next) => (
  prev.clip.start === next.clip.start &&
  prev.clip.duration === next.clip.duration &&
  prev.clip.name === next.clip.name &&
  prev.clip.color === next.clip.color &&
  prev.selected === next.selected &&
  prev.playing === next.playing &&
  prev.pixelsPerSecond === next.pixelsPerSecond &&
  prev.track.color === next.track.color
));

function colorWithAlpha(hex, alpha) {
  const m = hex.match(/^#([0-9a-f]{6})$/i);
  if (!m) return hex;
  const r = parseInt(m[1].slice(0, 2), 16);
  const g = parseInt(m[1].slice(2, 4), 16);
  const b = parseInt(m[1].slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
